// Writing a room back out as authored JSON -- the other half of load.ts.
//
// The one-time exporter that produced `data/*.json` from the old TypeScript
// builders formatted them a particular way, and it is gone (Phase 2). This is
// what keeps that formatting alive now that the editor is the thing writing
// rooms: a room saved out of the editor is byte-identical to the same room
// committed in `data/`, so re-saving an untouched room produces an empty diff and
// a real edit produces a diff about the edit. A writer that reformatted every
// file would make the world's history unreadable within a week.
//
// `sim.test.ts` holds it to that: it stringifies each of the 11 committed rooms
// and compares against the file on disk.
import { RoomDef } from '../types';
import { WORLD_FORMAT_VERSION } from './load';

/** One authored room as it belongs on disk: `formatVersion` first, two-space
 * indentation, and a trailing newline. */
export function stringifyRoom(room: RoomDef): string {
  return readableJson({ formatVersion: WORLD_FORMAT_VERSION, ...room });
}

/** Two-space JSON with a trailing newline, and the one readability rule this
 * project's data needs.
 *
 * JSON.stringify's indentation puts every tilemap number on its own line, which
 * turns a 15x15 room into 225 lines nobody can read or diff. Arrays holding only
 * numbers (a tilemap row) and {x,y} pairs (a conduit's path tiles) collapse onto
 * one line each. Cosmetic only -- what parses back is identical either way, which
 * is why the round-trip test can compare parsed content and the formatting test
 * can compare bytes, and neither has to know about the other.
 *
 * Shared with recordings (see sim/replay.ts), which embed a whole room. */
export function readableJson(value: unknown): string {
  const json = JSON.stringify(value, null, 2)
    .replace(/\[\s+((?:-?\d+,\s+)*-?\d+)\s+\]/g, (_, body: string) => `[${body.replace(/\s+/g, ' ')}]`)
    .replace(/\{\s+"x": (-?\d+),\s+"y": (-?\d+)\s+\}/g, '{ "x": $1, "y": $2 }');
  return `${json}\n`;
}
